import { Button } from '@quadminds/mindkit';
import { RiCloseCircleFill, RiRefreshLine } from '@remixicon/react';
import { CirclePlus, FileX } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import YpfIntegrationModalBase from './YpfIntegrationModalBase';
import YpfIntegrationModalSection from './YpfIntegrationModalSection';
import { getFileSize } from '../../../../utils/utils';
import { YpfImportResponse } from '../Connections/Connections';

interface YpfIntegrationResultProps {
    file: File | null;
    results: YpfImportResponse | null;
    isOpen: boolean;
    handleCloseModal: (value: boolean) => void;
}
const YpfIntegrationResult: FC<YpfIntegrationResultProps> = ({ file, results, isOpen, handleCloseModal }) => {
    const { t } = useTranslation();

    if (results === null) return null;
    if (file === null) return null;

    const dataSuccess = results && results.ok ? results.data : null;
    const dataError = results && !results.ok ? results.error.details?.code : null;

    return (
        <YpfIntegrationModalBase
            isOpen={isOpen}
            handleCloseModal={handleCloseModal}
            title={t('settings.connections.results.title')}
        >
            <YpfIntegrationModalSection>
                <div className="flex bg-[#37474F] p-3 rounded-[4px] h-[60px] items-center justify-start">
                    <div className="flex justify-start items-center">
                        <FileX className="h-[18px] w-[18px] text-[#D5C2FB] mr-3" />
                        <div className="flex flex-col gap-1">
                            <p className="font-roboto font-medium text-[14px] leading-[14px]">{file.name}</p>
                            <p className="font-roboto font-normal text-[12px] leading-[12px]">{getFileSize(file)}</p>
                        </div>
                    </div>
                </div>
            </YpfIntegrationModalSection>
            <YpfIntegrationModalSection>
                {dataSuccess && (
                    <div className="flex  bg-[#37474F] p-2.5 rounded-[4px] items-center justify-start">
                        <div className="flex">
                            <CirclePlus className="h-4  text-[#059669]" />
                            <p className="font-roboto text-[12px] leading-[14px] text-[#CFD8DC]">
                                {t('settings.connections.results.new', {
                                    count: dataSuccess.imported_rows.new_records,
                                })}
                            </p>
                        </div>
                    </div>
                )}
                {dataSuccess && (
                    <div className="flex  bg-[#37474F] p-2.5 rounded-[4px] items-center justify-start mt-4">
                        <div className="flex">
                            <RiRefreshLine className="h-4  text-[#059669]" />
                            <p className="font-roboto text-[12px] leading-[14px] text-[#CFD8DC]">
                                {t('settings.connections.results.update', {
                                    count: dataSuccess.imported_rows.updated_records,
                                })}
                            </p>
                        </div>
                    </div>
                )}
                {dataSuccess?.error_summary && dataSuccess.error_summary.total_errors > 0 && (
                    <div className="flex flex-col bg-[#37474F] p-3 rounded-[4px] items-start justify-start mt-4">
                        <div className="flex flex-col justify-end items-start gap-2 w-full">
                            <div className="flex">
                                <RiCloseCircleFill className="h-4 text-[#FCA5A5]" />
                                <p className="font-roboto text-[14px] leading-[14px] text-white">
                                    {t('settings.connections.results.error', {
                                        count: dataSuccess.error_summary.total_error_rows,
                                    })}
                                </p>
                            </div>
                            <div className="flex flex-col max-h-20 overflow-y-auto w-full">
                                {dataSuccess.error_summary.details.map((error, index) => (
                                    <p
                                        key={index}
                                        className="font-roboto text-[12px] leading-[14px] text-[#CFD8DC] mb-1"
                                    >
                                        {error.rowNumber > 0
                                            ? t(`settings.connections.error.${error.errorCode}`, {
                                                  row: error.rowNumber,
                                              })
                                            : t(`settings.connections.error.${error.errorCode}`)}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {dataError && (
                    <div className="flex flex-col bg-[#37474F] p-3 rounded-[4px] items-start justify-start mt-4">
                        <div className="flex flex-col justify-end items-center gap-2">
                            <div className="flex">
                                <RiCloseCircleFill className="h-4 text-[#FCA5A5]" />
                                <p className="font-roboto text-[14px] leading-[14px] text-white">
                                    {t(`settings.connections.error.${dataError}`)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </YpfIntegrationModalSection>
            <YpfIntegrationModalSection className="flex-row justify-end">
                <Button onClick={() => handleCloseModal(false)} className="mr-2">
                    {t('settings.connections.close')}
                </Button>
            </YpfIntegrationModalSection>
        </YpfIntegrationModalBase>
    );
};

export default YpfIntegrationResult;
